"use client";

import React, { useEffect, useState, useMemo, useRef } from "react";
import { Loader2, Activity, BarChart3, DollarSign, Info, Map } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import USMapSVG from "./USMapSVG";
import type { StateData } from "./USMapSVG";
import { getPolicyStatsByState } from "../services/policy-map-service";

interface PolicyMapInlineProps {
  open: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function PolicyMapInline({ open }: PolicyMapInlineProps) {
  const [data, setData] = useState<StateData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || hasFetched) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getPolicyStatsByState()
      .then((result) => {
        if (!cancelled) {
          setData(result || []);
          setHasFetched(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load policy map data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, hasFetched]);

  const stats = useMemo(() => {
    const totalPolicies = data.reduce((sum, d) => sum + d.count, 0);
    const activeStates = data.filter((d) => d.count > 0).length;
    const totalPremium = data.reduce((sum, d) => sum + Number(d.totalPremium || 0), 0);
    const issuedPremium = data.reduce((sum, d) => sum + Number(d.issuedPremium || 0), 0);
    const maxCount = Math.max(...data.map((d) => d.count), 1);
    return { totalPolicies, activeStates, totalPremium, issuedPremium, maxCount };
  }, [data]);

  return (
    <div
      className="overflow-hidden transition-all duration-500 ease-in-out"
      style={{
        maxHeight: open ? "2000px" : "0px",
        opacity: open ? 1 : 0,
      }}
    >
      <div ref={contentRef} className="policy-map rounded-xl p-6 mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        ) : (
          <>
            {/* Stats bar top */}
            <div className="pm-stats mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Total Policies
                  </span>
                </div>
                <p className="text-xl font-bold text-foreground">
                  {stats.totalPolicies.toLocaleString()}
                </p>
              </div>

              <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Map className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Active States
                  </span>
                </div>
                <p className="text-xl font-bold text-foreground">
                  {stats.activeStates}
                </p>
              </div>

              {/* Sold and production are separate tiles, matching the agency
                  dashboard — one label over two figures named only the larger. */}
              <div className="rounded-lg border border-border bg-muted/50 p-3 text-center">
                <div className="mb-1 flex items-center justify-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Total Premium Sold
                  </span>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="What counts as premium sold"
                          className="inline-flex cursor-help items-center align-middle"
                        >
                          <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground">
                        Only policies marked sold and approved.
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                {/* div, not p — TooltipContent renders a div and nesting one in
                    a p is invalid HTML (hydration error). */}
                <div className="text-xl font-bold text-emerald-600">
                  {formatCurrency(stats.issuedPremium)}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/50 p-3 text-center">
                <div className="mb-1 flex items-center justify-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Total Production
                  </span>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="What counts as total production"
                          className="inline-flex cursor-help items-center align-middle"
                        >
                          <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground">
                        Everything marked sold and approved as well as in underwriting.
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                <div className="text-xl font-bold text-foreground">
                  {formatCurrency(stats.totalPremium)}
                </div>
              </div>
            </div>

            {/* Map */}
            <USMapSVG data={data} maxCount={stats.maxCount} />

            {/* Color legend */}
            <div className="flex items-center justify-center gap-6 mt-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#e5e7eb" }} />
                <span className="text-xs text-muted-foreground">No policies</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: "color-mix(in srgb, var(--primary) 30%, transparent)" }}
                />
                <span className="text-xs text-muted-foreground">Low</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: "color-mix(in srgb, var(--primary) 65%, transparent)" }}
                />
                <span className="text-xs text-muted-foreground">Medium</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: "var(--primary)" }}
                />
                <span className="text-xs text-muted-foreground">High</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
